/**
 * Player names, and matching them.
 *
 * Two problems, both visible the moment you search 23 years of draft boards for "Steve Smith".
 *
 * 1. THE SAME PERSON IS SPELLED SEVERAL WAYS. The archive carries "Steve Smith", "Steve Smith
 *    (CAR)", "Michael Crabtree (7)" and "Maurice-Jones Drew". Some of that is punctuation noise,
 *    some is a hand-typed disambiguator, some is a typo. For league records — most keeper years,
 *    most times drafted — those have to collapse onto one person.
 *
 * 2. TWO PEOPLE SHARE A NAME. "Steve Smith" really is two players in this league's history, and
 *    the parenthetical is the only thing that tells them apart. So the cleanup must NOT merge
 *    everything that looks alike; it has to keep the distinguishing bit and throw away the noise.
 *
 * The rule here: strip the noise, keep an explicit `qualifier` when one is present, and treat two
 * rows as the same person only when their names agree AND their qualifiers do not contradict. A
 * bare "Steve Smith" is compatible with "Steve Smith (CAR)"; "(CAR)" and "(NYG)" are not.
 */

/** NFL team codes that show up as hand-typed disambiguators in the archive. */
const TEAM_CODES = new Set([
  'ARI', 'ATL', 'BAL', 'BUF', 'CAR', 'CHI', 'CIN', 'CLE', 'DAL', 'DEN', 'DET', 'GB', 'HOU',
  'IND', 'JAX', 'JAC', 'KC', 'LAC', 'LAR', 'LA', 'LV', 'MIA', 'MIN', 'NE', 'NO', 'NYG', 'NYJ',
  'OAK', 'PHI', 'PIT', 'SD', 'SEA', 'SF', 'STL', 'TB', 'TEN', 'WAS', 'WSH', 'AZ',
]);

/** Suffixes that are part of who somebody is, not decoration. */
const SUFFIXES = new Set(['JR', 'SR', 'II', 'III', 'IV', 'V']);

export interface CleanedName {
  /** Display form: punctuation normalised, junk removed, qualifier kept in parentheses. */
  name: string;
  /** The name with nothing after it — what a human would type. */
  base: string;
  /** A team code that was attached to tell two players apart, if there was one. */
  qualifier?: string;
  /** Suffix, kept separate so "Steve Smith Sr" and "Steve Smith" can be reasoned about. */
  suffix?: string;
  /** Anything removed that was neither, e.g. the stray "(7)" on a 2012 row. */
  dropped?: string;
}

/**
 * Clean one archive name.
 *
 * Periods and commas go (every feed disagrees about "A.J."); hyphens and apostrophes stay. A
 * trailing parenthetical is inspected rather than assumed: a team code becomes the qualifier, a
 * bare number is spreadsheet debris and is recorded in `dropped` so it is not silently lost.
 */
export function cleanPlayerName(raw: string): CleanedName {
  let s = String(raw ?? '').replace(/\s+/g, ' ').trim();
  let qualifier: string | undefined;
  let dropped: string | undefined;

  // Pull off every trailing parenthetical, newest first.
  let m = /\s*\(([^)]*)\)\s*$/.exec(s);
  while (m) {
    const inner = m[1]!.trim();
    const upper = inner.toUpperCase().replace(/[^A-Z]/g, '');
    if (TEAM_CODES.has(upper)) qualifier ??= upper;
    else dropped = dropped ? `${inner}; ${dropped}` : inner;
    s = s.slice(0, m.index).trim();
    m = /\s*\(([^)]*)\)\s*$/.exec(s);
  }

  s = s.replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();

  // Suffix, if the last token is one.
  let suffix: string | undefined;
  const parts = s.split(' ');
  const lastUpper = (parts[parts.length - 1] ?? '').toUpperCase();
  if (parts.length > 2 && SUFFIXES.has(lastUpper)) {
    suffix = titleSuffix(lastUpper);
    parts.pop();
    s = parts.join(' ');
  }

  const base = s;
  const name = [base, suffix, qualifier ? `(${qualifier})` : ''].filter(Boolean).join(' ');
  return {
    name, base, ...(qualifier ? { qualifier } : {}), ...(suffix ? { suffix } : {}),
    ...(dropped ? { dropped } : {}),
  };
}

const titleSuffix = (s: string) => (s === 'JR' ? 'Jr' : s === 'SR' ? 'Sr' : s);

export const nameKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The identity two rows have to share to be counted as one player in a record.
 *
 * Suffix is part of it — Steve Smith Sr is a different person from Steve Smith Jr, and the league
 * has drafted both. The qualifier is NOT part of it, because most rows do not carry one; it is
 * used by `samePlayer` to rule a merge out rather than to key it.
 */
export function identityKey(c: CleanedName): string {
  return nameKey(`${c.base}${c.suffix ?? ''}`);
}

/** Compatible unless both carry a qualifier and the qualifiers differ. */
export function samePlayer(a: CleanedName, b: CleanedName): boolean {
  if (identityKey(a) !== identityKey(b)) return false;
  if (a.qualifier && b.qualifier && a.qualifier !== b.qualifier) return false;
  return true;
}

/* -- the same question, asked of two FEEDS rather than of two archive rows ---------------------- */

/**
 * WHY THERE IS A SECOND, LOOSER KEY, and why `identityKey` must not be relaxed to be it.
 *
 * `identityKey` answers a question about the LEAGUE ARCHIVE, where Steve Smith Sr and Steve Smith
 * are two men the league has actually drafted, and collapsing them would rewrite history. It
 * therefore keeps the suffix.
 *
 * `feedKey` answers a different question about the PLAYER BANK, where the two rows are not two
 * men — they are one man arriving from two feeds that disagree about whether to print "Jr".
 * nflverse's roster CSV says "Chris Hilton", its player CSV says "Chris Hilton Jr", Sleeper says
 * "Odell Beckham" where nflverse says "Odell Beckham Jr", and every one of those pairs became two
 * rows on the draft board — one of them a phantom free agent, because the merge could not see
 * that he was already there. Fifteen of them shipped in the 13 August snapshot.
 *
 * The looseness is bounded by `sameFeedPlayer`: the bases must match AND the suffixes must not
 * CONTRADICT. A bare name absorbs a suffixed one; two different suffixes stay two men, which is
 * the case `identityKey` exists to protect.
 */
export const feedKey = (c: CleanedName) => nameKey(c.base);

/** One man under two spellings? Only when nothing they both state disagrees. */
export function sameFeedPlayer(a: CleanedName, b: CleanedName): boolean {
  if (feedKey(a) !== feedKey(b)) return false;
  if (a.suffix && b.suffix && a.suffix !== b.suffix) return false;
  if (a.qualifier && b.qualifier && a.qualifier !== b.qualifier) return false;
  return true;
}

/**
 * Of two spellings of one man, the one to print.
 *
 * THE SUFFIX WINS, always. It is the half of the name that distinguishes him from his father, and
 * a board that drops it has quietly made two people ambiguous to save three characters. Beyond
 * that, prefer the longer form — "Amon-Ra St Brown" over "AmonRa St Brown" — and break a true tie
 * on the first argument so the result does not depend on iteration order.
 */
export function fullerName(a: string, b: string): string {
  const [ca, cb] = [cleanPlayerName(a), cleanPlayerName(b)];
  if (ca.suffix && !cb.suffix) return a;
  if (cb.suffix && !ca.suffix) return b;
  if (a.length !== b.length) return a.length > b.length ? a : b;
  return a;
}

/* ------------------------------------------------------------------------------------------- */

/**
 * Wildcard-aware matching.
 *
 * `%` means "anything here", the way it does in SQL, because that is the wildcard people who use
 * spreadsheets already know. `zac% miller` finds Zach and Zack; `%jones` finds every Jones. A
 * query with no `%` behaves exactly as before — a plain substring match — so nobody has to learn
 * anything to keep using it.
 */
export function matcher(query: string): (candidate: string) => boolean {
  const q = nameKeySpaced(query).trim();
  if (!q) return () => true;
  if (!q.includes('%')) return (c) => nameKeySpaced(c).includes(q);

  const pattern = q
    .split('%')
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('.*');
  const re = new RegExp(pattern);
  return (c) => re.test(nameKeySpaced(c));
}

/** Lowercase, punctuation out, spaces kept — `%` survives so the matcher can see it. */
const nameKeySpaced = (s: string) => s.toLowerCase().replace(/[^a-z0-9% ]/g, '');

export const hasWildcard = (q: string) => q.includes('%');
